import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';

const AuditType = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canAuditType = (key) => can(key, 'auditType');

  // Remove all modal refs and instances - use pure React approach

  // Pagination
  const pagination = usePagination(10);

  // Search
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  // Selection
  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  // Date filter
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Data and form state
  const [auditTypes, setAuditTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    certificationSchemeIds: [],
    activityType: '',
    productCategory: '',
    level: '',
    serviceIds: '',
    year: 'every',
  });
  const [productCategories, setProductCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [services, setServices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [certificationSchemes, setCertificationSchemes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const prefillingRef = useRef(false);
  // Activity type options
  const ACTIVITY_TYPES = [
    { id: 'administration', name: 'Administration' },
    { id: 'activity', name: 'Activity' }
  ];

  const YEAR_OPTIONS = [
    { id: '0', name: 'Every Year' },
    { id: '1', name: '1st Year' },
    { id: '2', name: '2nd Year' },
    { id: '3', name: '3rd Year' },
    { id: '4', name: '4th Year' },
    { id: '5', name: '5th Year' },
    { id: '6', name: '6th Year' },
  ];

  // Reset form data helper function
  const resetFormData = useCallback(() => {
    setFormData({
      name: '',
      certificationSchemeIds: [],
      activityType: '',
      productCategory: '',
      level: '',
      serviceIds: '',
      year: 'every',
    });
    setErrors({});
    setEditingId(null);
    setLevels([]);
  }, []);

  // Fetch audit types
  const fetchAuditTypes = async (searchValue = appliedSearchTerm) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/md/audit-type/page`, {
        params: {
          page: pagination.page,
          size: pagination.size,
          search: searchValue,
          from: fromDate,
          to: toDate
        }
      });
      setAuditTypes(response.data.content);
      pagination.setTotalPages(response.data.totalPages);
      pagination.setTotal(response.data.totalElements);
      pagination.setCurrentPageItems(response.data.content.length);
    } catch (error) {
      let errorMessage = 'Failed to fetch audit types.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view audit types.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching audit types.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching audit types.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setAuditTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Product Categories (filtered by serviceId)
  const fetchProductCategories = async (serviceId = null) => {
    if (!serviceId) {
      setProductCategories([]);
      return [];
    }
    try {
      const response = await api.get(`/md/product-category-options-by-service/${serviceId}`);
      setProductCategories(response.data);
      return response.data;
    } catch (error) {
      let errorMessage = 'Failed to fetch product categories.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view product categories.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server error while fetching product categories.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching product categories.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred while fetching product categories.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setProductCategories([]);
      return [];
    }
  };
  
  // Fetch Levels
  const fetchLevels = async () => {
    try {
      const response = await api.get('/md/level');
      setLevels(response.data);
    } catch (error) {
      let errorMessage = 'Failed to fetch levels.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view levels.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server error while fetching levels.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching levels.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred while fetching levels.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setLevels([]);
    }
  };

  // Fetch Scheme Categorys
  const fetchCertificationSchemes = async () => {
    try {
      const response = await api.get('/md/certification-scheme');
      setCertificationSchemes(response.data);
    } catch (error) {
      let errorMessage = 'Failed to fetch certification schemes.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view certification schemes.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server error while fetching certification schemes.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching certification schemes.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setCertificationSchemes([]);
    }
  };

  // Fetch Services
  const fetchServices = async () => {
    try {
      const response = await api.get('/md/company-service');
      setServices(response.data);
    } catch (error) {
      let errorMessage = 'Failed to fetch services.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view services.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server error while fetching services.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching services.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setServices([]);
    }
  };

  const fetchProductLevels = async (productCategoryId = null) => {
    try {
      let response;
      if (productCategoryId) {
        response = await api.get(`/md/product-level-options-by-category/${productCategoryId}`);
      } else {
        response = await api.get('/md/product-level-options');
      }
      setLevels(response.data);
      return response.data;
    } catch (error) {
      let errorMessage = 'Failed to fetch product levels.';
      let technicalDetails = '';
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view product levels.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server error while fetching product levels.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching product levels.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred while fetching product levels.';
        technicalDetails = error.message;
      }
      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setLevels([]);
      return [];
    }
  };

  useEffect(() => {
    if (ACTIVITY_TYPES.length > 0 && !formData.activityType) {
      setFormData(prev => ({
        ...prev,
        activityType: ACTIVITY_TYPES[0].id
      }));
    }
  }, [ACTIVITY_TYPES]);

  useEffect(() => {
    fetchAuditTypes(appliedSearchTerm);
    fetchCertificationSchemes();
    fetchServices();
  }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

  // When Services selection changes
  useEffect(() => {
    const selectedServiceId = formData.serviceIds;
    if (prefillingRef.current) {
      return; // don't clear values while pre-filling in edit mode
    }
    if (selectedServiceId) {
      setFormData(prev => ({ ...prev, productCategory: '', level: '' }));
      setErrors(prev => ({ ...prev, productCategory: null, level: null }));
      setLevels([]);
      fetchProductCategories(selectedServiceId);
    } else {
      setProductCategories([]);
      setFormData(prev => ({ ...prev, productCategory: '', level: '' }));
      setErrors(prev => ({ ...prev, productCategory: null, level: null }));
      setLevels([]);
    }
  }, [formData.serviceIds]);

  // Simplified modal handlers - no Bootstrap JS, pure React
  const handleCreate = () => {
    if (!canAuditType('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    resetFormData();
    setEditingId(null);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-audit-type'));
    modal.show();
  };

  const handleEdit = async (item) => {
    if (!canAuditType('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    prefillingRef.current = true;
    setFormData({
      name: item.name,
      certificationSchemeIds: item.certificationSchemes?.map(scheme => scheme.id) || [],
      activityType: item.activityType || '',
      productCategory: '',
      level: '',
      serviceIds: item.serviceId || '',
      year: item.year || 'every',
    });
    setEditingId(item.id);
    setErrors({});

    if (item.serviceId) {
      await fetchProductCategories(item.serviceId);
    } else {
      setProductCategories([]);
    }
    setFormData(prev => ({ ...prev, productCategory: item.productCategory || '' }));

    if (item.productCategory) {
      await fetchProductLevels(item.productCategory);
    } else {
      setLevels([]);
    }
    setFormData(prev => ({ ...prev, level: item.level || '' }));

    const modal = new window.bootstrap.Modal(document.getElementById('modal-audit-type'));
    modal.show();

    // release the guard after React applied updates
    setTimeout(() => { prefillingRef.current = false; }, 0);
  };

  const closeModal = () => {
    const modalEl = document.getElementById('modal-audit-type');
    const modalInstance = window.bootstrap?.Modal.getInstance(modalEl);
    if (modalInstance) modalInstance.hide();
    resetFormData();
    setEditingId(null);
  };

  // Real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'name') {
      error = validateField('name', value, { required: true });
    } else if (name === 'activityType') {
      error = validateField('activityType', value, { required: true });
    } else if (name === 'productCategory') {
      error = validateField('productCategory', value, { required: true });
    } else if (name === 'level') {
      error = validateField('level', value, { required: true });
    } else if (name === 'year') {
      error = validateField('year', value, { required: true });
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // TomSelect change handler
  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'certificationSchemeIds') {
      error = value.length === 0 ? 'At least one Scheme Category is required' : null;
    } else if (name === 'activityType') {
      error = !value ? 'Activity Type is required' : null;
    } else if (name === 'year') {
      error = validateField('year', value, { required: true });
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleProductCategoryChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    let error = validateField(name, value, { required: true });
    setErrors(prev => ({ ...prev, [name]: error }));

    if (name === 'productCategory' && value) {
      setFormData(prev => ({ ...prev, level: '' }));
      setErrors(prev => ({ ...prev, level: null }));
      setLevels([]);
      setTimeout(() => {
        fetchProductLevels(value);
      }, 100);
    } else if (name === 'productCategory' && !value) {
      setLevels([]);
      setFormData(prev => ({ ...prev, level: '' }));
      setErrors(prev => ({ ...prev, level: null }));
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    let error = validateField('name', formData.name, { required: true });
    let schemeError = formData.certificationSchemeIds.length === 0 ? 'At least one Scheme Category is required' : null;
    let productCategoryError = validateField('productCategory', formData.productCategory, { required: true });
    let levelError = validateField('level', formData.level, { required: true });
    let yearError = validateField('year', formData.year, { required: true });

    setErrors({
      name: error,
      certificationSchemeIds: schemeError,
      productCategory: productCategoryError,
      level: levelError,
      year: yearError,
    });

    if (error || schemeError || productCategoryError || levelError || yearError) return;

    try {
      const payload = {
        name: formData.name,
        certificationSchemeIds: formData.certificationSchemeIds,
        activityType: formData.activityType,
        productCategory: formData.productCategory,
        level: formData.level,
        serviceId: formData.serviceIds || null,
        year: formData.year,
      };

      if (editingId) {
        await api.put(`/md/audit-type/${editingId}`, payload);
        setSuccessMessage('Audit type has been successfully updated.');
      } else {
        await api.post('/md/audit-type', payload);
        setSuccessMessage('Audit type has been successfully added.');
      }

      await fetchAuditTypes();
      closeModal();
      setTimeout(() => setShowSuccessModal(true), 200);
    } catch (error) {
      let errorMessage = 'Failed to save audit type.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid audit type data provided.';
            technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 409:
            errorMessage = 'An audit type with this name already exists.';
            technicalDetails = `Duplicate entry for name: "${formData.name}"`;
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the audit type.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  // Edit selected
  const editSelected = () => {
    if (selectedIds.length !== 1) {
      setSuccessMessage('Please select exactly one audit type to edit');
      setShowSuccessModal(true);
      return;
    }
    const selectedId = selectedIds[0];
    const selectedItem = auditTypes.find(item => item.id === selectedId);
    if (selectedItem) {
      handleEdit(selectedItem);
    }
  };

  // Delete selected
  const deleteSelected = () => {
    if (!canAuditType('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (selectedIds.length === 0) {
      setFailMessage('Please select at least one audit type to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        await api.delete('/md/audit-type/bulk', { data: selectedIds });
        setSelectedIds([]);
        fetchAuditTypes();
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected audit types deleted successfully');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete audit types.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more audit types not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'certificationSchemes', label: 'Scheme Categories', sortable: false, render: (value) => Array.isArray(value) ? value.map(s => s.name).join(', ') : (value || '-') },
    { key: 'activityType', label: 'Activity Type', sortable: true },
    { key: 'productCategoryName', label: 'Product Category', sortable: true, render: (value) => value || '-' },
    { key: 'levelName', label: 'Level', sortable: true, render: (value) => value || '-' },
    { key: 'year', label: 'Year', sortable: true, render: (value) => YEAR_OPTIONS.find(opt => opt.id === value)?.name || '' },
    { key: 'serviceName', label: 'Services', sortable: true, render: (value) => value || '-' },
    { key: 'cuser', label: 'Created By', sortable: true, render: (value) => value?.username || '-' },
    { key: 'cdate', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleString() : '-' },
  ];

  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Audit Type / Service Item</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={handleCreate}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24"
                    strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <GenericView
                data={auditTypes}
                columns={columns}
                isLoading={isLoading || capabilitiesLoading}
                emptyStateMessage="No audit types found"
                checkboxSelection={true}
                selectedRows={selectedIds}
                onSelectRow={(itemId, isSelected) => {
                  if (isSelected) {
                    setSelectedIds([...selectedIds, itemId]);
                  } else {
                    setSelectedIds(selectedIds.filter(id => id !== itemId));
                  }
                }}
                onSelectAllRows={(itemIds) => {
                  if (Array.isArray(itemIds) && itemIds.length > 0) {
                    setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                  } else {
                    setSelectedIds([]);
                  }
                }}
                onEdit={canAuditType('edit') ? (item) => {
                  setSelectedIds([item.id]);
                  handleEdit(item);
                } : undefined}
                onDelete={canAuditType('delete') ? (item) => {
                  setSelectedIds([item.id]);
                  deleteSelected();
                } : undefined}
                searchComponent={
                  <div className="d-flex gap-2">
                    <SearchInput
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchSubmit();
                        }
                      }}
                      placeholder="Search..."
                      uniqueId="audit-type-search"
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSearchSubmit}
                    >
                      Search
                    </button>
                  </div>
                }
                actionButtons={
                  <div className="d-flex gap-2">
                    {(canAuditType('edit') || canAuditType('delete')) && (
                      <div ref={actionsContainerRef} className="dropdown">
                        <a
                          ref={actionsToggleRef}
                          href="#"
                          className="btn dropdown-toggle"
                          data-bs-toggle="dropdown"
                          aria-expanded={isActionsOpen}
                          onClick={(e) => {
                            e.preventDefault();
                            const inst = actionsDropdownInstanceRef.current;
                            if (inst?.toggle) inst.toggle();
                            else setIsActionsOpen(prev => !prev);
                          }}
                        >Actions</a>
                        <div className="dropdown-menu">
                          {canAuditType('edit') && (
                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                              style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                              Edit
                            </a>
                          )}
                          {canAuditType('delete') && (
                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                              style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                              Delete
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                }
                pagination={{
                  currentPage: pagination.page + 1,
                  totalRows: pagination.total,
                  rowsPerPage: pagination.size,
                  onPageChange: (page) => pagination.setPage(page - 1),
                  onRowsPerPageChange: (size) => {
                    pagination.setSize(size);
                    pagination.setPage(0);
                  }
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                uniqueId="audit-type-table"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal */}
      <div className="modal modal-blur fade" id="modal-audit-type" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingId ? 'Edit Audit Type / Service Item' : 'Add New Audit Type / Service Item'}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={closeModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Activity Type</label>
                  <div>
                    {ACTIVITY_TYPES.map(type => (
                      <label key={type.id} className="form-check form-check-inline">
                        <input
                          required={true}
                          className="form-check-input"
                          type="radio"
                          name="activityType"
                          value={type.id}
                          checked={formData.activityType === type.id}
                          onChange={handleInputChange}
                        />
                        <span className="form-check-label">{type.name}</span>
                      </label>
                    ))}
                  </div>
                  {errors.activityType && (
                    <div className="invalid-feedback d-block">{errors.activityType}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label required">Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter name"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                {/* TomSelect Components with unique keys to prevent DOM conflicts */}
                <div className="mb-3">
                  <TomSelect
                    name="certificationSchemeIds"
                    value={formData.certificationSchemeIds}
                    onChange={handleSelectChange}
                    options={certificationSchemes.map(scheme => ({ id: scheme.id, name: scheme.name }))}
                    placeholder="Select Scheme Categories"
                    label="Scheme Categories"
                    required={true}
                    error={errors.certificationSchemeIds}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    multiple={true}
                  />
                </div>

                <div className="mb-3">
                  <TomSelect
                    name="year"
                    value={formData.year}
                    onChange={handleSelectChange}
                    options={YEAR_OPTIONS}
                    placeholder="Select Year"
                    label="Year"
                    required={true}
                    error={errors.year}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                  />
                </div>

                <div className="mb-3">
                  <TomSelect
                    name="serviceIds"
                    value={formData.serviceIds}
                    onChange={handleSelectChange}
                    required={true}
                    options={services.map(service => ({ id: service.id, name: service.name }))}
                    placeholder="Select Services"
                    label="Services"
                    valueField="id"
                    labelField="name"
                    searchField="name"
                  />
                </div>

                <div className="mb-3">
                  <TomSelect
                    name="productCategory"
                    value={formData.productCategory}
                    onChange={handleProductCategoryChange}
                    placeholder="Enter product category"
                    options={productCategories.map(category => ({ id: category.id, name: category.name }))}
                    required={true}
                    error={errors.productCategory}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    label="Product Category"
                  />
                </div>

                <div className="mb-3">
                  <TomSelect
                    name="level"
                    value={formData.level}
                    onChange={handleProductCategoryChange}
                    options={levels.map(level => ({ id: level.id, name: level.name }))}
                    placeholder="Enter product level"
                    required={true}
                    error={errors.level}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    label="Product Level"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary ms-auto">
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Other Modals */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction || (() => { })}
        title="Delete Confirmation"
        message={selectedIds.length > 0
          ? "Are you sure you want to delete the selected audit types? This action cannot be undone."
          : "Are you sure you want to delete this audit type? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default AuditType;